import { ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { BigNumber, toBigNumber } from '@injectivelabs/utils/big-number'
import { calculateScaledMarkPrice } from '@/app/client/utils/derivatives'
import type { BffDerivativeMarket } from '@shared/types'
import type { OraclePriceV2Filter } from '@injectivelabs/sdk-ts/client/indexer'
import type {
  MarketMarkPriceMap,
  MarketIndexPriceMap,
  MarketDisplayPriceMap
} from '@/types'

type MarketPriceMap = MarketMarkPriceMap | MarketIndexPriceMap

export function derivativeMarketsToOraclePriceV2Filters(
  markets: BffDerivativeMarket[]
): OraclePriceV2Filter[] {
  return markets.flatMap((market) => {
    if (!market.oracleBase || !market.oracleQuote) {
      return []
    }

    return [
      {
        oracleType: market.oracleType,
        baseSymbol: market.oracleBase,
        quoteSymbol: market.oracleQuote,
        oracleScaleFactor: market.oracleScaleFactor
      }
    ]
  })
}

export function getScaledMarketPriceFromMap({
  market,
  priceMap
}: {
  priceMap: MarketPriceMap
  market: BffDerivativeMarket
}): BigNumber | undefined {
  const price = priceMap[market.marketId]?.price

  return getScaledMarketPrice({ market, price })
}

export function getScaledMarketPrice({
  price,
  market
}: {
  price?: null | string
  market: BffDerivativeMarket
}): BigNumber | undefined {
  const priceInBigNumber = getValidPriceInBigNumber(price)

  if (!priceInBigNumber) {
    return undefined
  }

  return calculateScaledMarkPrice({
    market,
    markPriceNotScaled: priceInBigNumber
  })
}

export function getScaledMarketDisplayPrice({
  price,
  market
}: {
  price?: null | string
  market: BffDerivativeMarket
}): string | undefined {
  const scaledPrice = getScaledMarketPrice({ price, market })

  if (!scaledPrice) {
    return undefined
  }

  return scaledPrice.toFixed(market.priceDecimals, BigNumber.ROUND_DOWN)
}

export function getScaledMarketDisplayPriceFromMap({
  market,
  priceMap
}: {
  priceMap: MarketPriceMap
  market: BffDerivativeMarket
}): string | undefined {
  return getScaledMarketDisplayPrice({
    market,
    price: priceMap[market.marketId]?.price
  })
}

export function getResolvedMarketDisplayLastPrice({
  market,
  fallbackPrice,
  marketMarkDisplayPriceMap,
  marketIndexDisplayPriceMap
}: {
  market: BffDerivativeMarket
  fallbackPrice?: null | string | BigNumber
  marketMarkDisplayPriceMap: MarketDisplayPriceMap
  marketIndexDisplayPriceMap: MarketDisplayPriceMap
}): BigNumber {
  return getResolvedDisplayLastPrice({
    fallbackPrice,
    indexPrice: marketIndexDisplayPriceMap[market.marketId],
    markPrice: marketMarkDisplayPriceMap[market.marketId]
  })
}

export function getResolvedDisplayLastPrice({
  markPrice,
  indexPrice,
  fallbackPrice
}: {
  markPrice?: null | string
  indexPrice?: null | string
  fallbackPrice?: null | string | BigNumber
}): BigNumber {
  const indexPriceInBigNumber = getValidPriceInBigNumber(indexPrice)

  if (indexPriceInBigNumber) {
    return indexPriceInBigNumber
  }

  const markPriceInBigNumber = getValidPriceInBigNumber(markPrice)

  if (markPriceInBigNumber) {
    return markPriceInBigNumber
  }

  return getValidPriceInBigNumber(fallbackPrice) ?? ZERO_IN_BIG_NUMBER
}

export function getResolvedMarketLastPrice({
  market,
  fallbackPrice,
  marketMarkPriceMap,
  marketIndexPriceMap
}: {
  market: BffDerivativeMarket
  marketMarkPriceMap: MarketMarkPriceMap
  marketIndexPriceMap: MarketIndexPriceMap
  fallbackPrice?: null | string | BigNumber
}): BigNumber {
  const indexPrice = getScaledMarketPriceFromMap({
    market,
    priceMap: marketIndexPriceMap
  })

  if (indexPrice) {
    return indexPrice
  }

  const markPrice = getScaledMarketPriceFromMap({
    market,
    priceMap: marketMarkPriceMap
  })

  if (markPrice) {
    return markPrice
  }

  return getValidPriceInBigNumber(fallbackPrice) ?? ZERO_IN_BIG_NUMBER
}

function getValidPriceInBigNumber(price?: null | string | BigNumber) {
  if (!price) {
    return undefined
  }

  const priceInBigNumber = toBigNumber(price)

  if (priceInBigNumber.isNaN()) {
    return undefined
  }

  return priceInBigNumber
}
